import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import PromoCodeForm from './PromoCodeForm';
import PromoCodeTypeBadges, { promoCodeAppliesDiscount } from './PromoCodeTypeBadges';
import PromoCodeCSVImportButton from '../../CSVImporter/PromoCodeCSVImportButton';
import {
  Alert, Badge, Button, CellText, Copy01, copyText, DotsVertical, Edit01, EmptyState, Link01, Menu, Pagination, Percent03, Plus, Select, Spinner, Table, Trash01,
} from '@opendate-app/setlist';

const axios = require("axios").default;

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PER_PAGE_OPTIONS = [10, 25, 50, 100].map((value) => ({ value, label: `${value} Rows` }));

const PromoCodesTab = ({
  currentUser,
  csrfToken,
  confirm,
  showActivities,
  team,
  ticketTypes,
  holds,
  generalAdmissionHolds,
  fetchGeneralAdmissionHolds,
  fetchHolds
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingDefaults, setIsAddingDefaults] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [missingDefaultNames, setMissingDefaultNames] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [promoCodeToCopy, setPromoCodeToCopy] = useState({});
  const [promoCodeToEdit, setPromoCodeToEdit] = useState({});
  const [promoCodes, setPromoCodes] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const missingDefaultsRequestId = useRef(0);

  const defaultPromoCodesPath = () => "/teams/" + team.id + "/confirms/" + confirm.id + "/default_promo_codes";

  const getMissingDefaults = () => {
    const requestId = ++missingDefaultsRequestId.current;
    axios.get(defaultPromoCodesPath())
      .then(({ data }) => {
        if (requestId === missingDefaultsRequestId.current) {
          setMissingDefaultNames(data.missing_template_names);
        }
      })
      .catch(() => {
        if (requestId === missingDefaultsRequestId.current) {
          setMissingDefaultNames([]);
        }
      });
  };

  const addDefaultPromoCodes = () => {
    setIsAddingDefaults(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(defaultPromoCodesPath())
      .then(({ data: { added_names, skipped_template_names } }) => {
        if (added_names.length > 0) {
          toast.success("Added " + added_names.join(", "), {
            position: toast.POSITION.TOP_CENTER,
            draggable: false,
            closeOnClick: false,
            autoClose: 3000,
            hideProgressBar: true
          });
        }
        if (skipped_template_names.length > 0) {
          toast.error("Couldn't add " + skipped_template_names.join(", ") + ". This usually means the event is missing the show details their dates are based on.", {
            position: toast.POSITION.TOP_CENTER,
            draggable: false,
            closeOnClick: false,
            autoClose: 8000,
            hideProgressBar: true
          });
        }
      })
      .catch(() => {
        toast.error("Couldn't add the default promo codes", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      })
      .finally(() => {
        setIsAddingDefaults(false);
        getPromoCodes(confirm, currentPage);
      });
  };

  const getPromoCodes = (confirm, page) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    getMissingDefaults();
    axios.get("/teams/" + team.id + "/promo_codes", {
        params: {
          confirm_id: confirm.id,
          page: page,
          per_page: perPage
        }
      })
      .then(data => {
        const { data: { collection, total_entries } } = data;
        setIsLoading(false);
        setTotalEntries(total_entries);
        setTotalPages(Math.ceil(total_entries / perPage));
        setPromoCodes(collection);
      })
      .catch(err => {
        setIsLoading(false);
        setPromoCodes([]);
      });
  };

  const deletePromoCode = (promoCode) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.delete("/teams/" + team.id + "/promo_codes/" + promoCode.id)
      .then(() => {
        if(promoCodes.length === 1) {
          getPromoCodes(confirm, 1);
          setCurrentPage(1);
        } else {
          getPromoCodes(confirm, currentPage);
        }
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const clearForm = () => {
    setIsEditing(false);
    setPromoCodeToEdit({});
    setPromoCodeToCopy({});
    getPromoCodes(confirm, currentPage);
  };

  const copyShareableUrl = (promoCode) => {
    const base = confirm.public_ticketing_url || "";
    const url = base + (base.includes("?") ? "&" : "?") + "promo=" + encodeURIComponent(promoCode.name);
    if (!navigator.clipboard) {
      toast.error("Could not copy link", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
      return;
    }
    copyText(url).then((ok) => {
      if (!ok) throw new Error('copy failed');
      toast.success("Shareable link copied", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 3000,
        hideProgressBar: true
      });
    }).catch(() => {
      toast.error("Could not copy link", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    });
  };

  const setIsOpen = (open) => {
    setIsEditing(open);

    if (!open) {
      setPromoCodeToEdit({});
      setPromoCodeToCopy({});
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getPromoCodes(confirm, currentPage);
  }, [currentPage, perPage])

  const openNew = () => setIsEditing(true);

  const importButton = (
    <PromoCodeCSVImportButton
      csrfToken={csrfToken}
      team={team}
      calendarEventId={confirm.id}
      onClose={() => getPromoCodes(confirm, currentPage)}
    />
  );

  const columns = [
    { key: "name", header: "Name", primary: true, render: (promoCode) => <CellText primary={promoCode.name} /> },
    {
      key: "type",
      header: "Code Type",
      render: (promoCode) => (
        <PromoCodeTypeBadges reveals={promoCode.reveal_hidden_tickets} discounts={promoCodeAppliesDiscount({ revealsHiddenTickets: promoCode.reveal_hidden_tickets, percentage: promoCode.percentage, amount: promoCode.amount })} />
      ),
    },
    {
      key: "discount",
      header: "Discount",
      render: (promoCode) => (
        !promoCodeAppliesDiscount({ revealsHiddenTickets: promoCode.reveal_hidden_tickets, percentage: promoCode.percentage, amount: promoCode.amount }) ? "—"
          : typeof(promoCode.percentage) === "number" ? promoCode.percentage + "%" : formatter.format(promoCode.amount)
      ),
    },
    {
      key: "uses",
      header: "Uses",
      render: (promoCode) => promoCode.number_claimed + "/" + (promoCode.unlimited ? "Unlimited" : promoCode.limit) + (promoCode.uses_per_fan ? ` · ${promoCode.uses_per_fan} per fan` : ""),
    },
    {
      key: "status",
      header: "Status",
      render: (promoCode) => (
        <CellText
          primary={
            <Badge dot size="sm" color={promoCode.active ? "success" : "error"}>
              {promoCode.active ? "Active" : "Inactive"}
            </Badge>
          }
          supporting={
            promoCode.active ? (
              "Ends " + promoCode.human_end_time
            ) : promoCode.past ? (
              "Ended " + promoCode.human_end_time
            ) : (
              "Starts " + promoCode.human_start_time
            )
          }
        />
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      width: "56px",
      render: (promoCode) => (
        <Menu
          placement="bottom-end"
          minWidth={200}
          items={[
            { label: "Edit", icon: Edit01, onClick: () => { setPromoCodeToEdit(promoCode); setIsEditing(true); } },
            { label: "Copy", icon: Copy01, onClick: () => { setPromoCodeToCopy(promoCode); setIsEditing(true); } },
            { label: "Share Link", icon: Link01, onClick: () => copyShareableUrl(promoCode) },
            { divider: true },
            { label: "Delete", icon: Trash01, destructive: true, onClick: () => deletePromoCode(promoCode) },
          ]}
          trigger={
            <Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${promoCode.name}`} />
          }
        />
      ),
    },
  ];

  const missingDefaultsAlert = missingDefaultNames.length > 0 && (
    <div className="mt-4">
      <Alert
        color="brand"
        title={missingDefaultNames.length === 1
          ? "1 of this venue's default promo codes isn't on this event"
          : missingDefaultNames.length + " of this venue's default promo codes aren't on this event"}
        description={missingDefaultNames.join(", ")}
        actions={
          <Button variant="secondary" size="sm" icon={Plus} disabled={isAddingDefaults} onClick={addDefaultPromoCodes}>
            Add Default Promo Codes
          </Button>
        }
      />
    </div>
  );

  return (
    <>
      {missingDefaultsAlert}
      {promoCodes.length === 0 ? (
        <div className="mt-5">
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner aria-label="Loading promo codes" />
            </div>
          ) : (
            <>
              <EmptyState
                icon={Percent03}
                title="No promo codes yet"
                description="Use promo codes to provide special discounts on tickets."
                primaryAction={{ label: "Add Promo Code", icon: Plus, onClick: openNew }}
              />
              <div className="d-flex justify-content-center mt-2">
                {importButton}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <div className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
            {importButton}
            <Button variant="secondary" size="sm" icon={Plus} onClick={openNew}>
              Add a Code
            </Button>
          </div>
          <div className="mt-4">
            <Table
              columns={columns}
              data={promoCodes}
              getRowKey={(promoCode) => promoCode.id}
              loading={isLoading}
              stackBelow={640}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center py-3" style={{ gap: "16px" }}>
            <div style={{ flex: 1 }}>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  prevNext="icon"
                />
              )}
            </div>
            <Select
              size="sm"
              width={130}
              aria-label="Rows per page"
              value={perPage}
              options={PER_PAGE_OPTIONS}
              onChange={(value) => {
                setCurrentPage(1);
                setPerPage(value);
              }}
            />
          </div>
        </div>
      )}
      <PromoCodeForm
        currentUser={currentUser}
        confirm={confirm}
        csrfToken={csrfToken}
        isOpen={isEditing}
        setIsOpen={setIsOpen}
        promoCodeToCopy={promoCodeToCopy}
        promoCodeToEdit={promoCodeToEdit}
        onClose={clearForm}
        showActivities={showActivities}
        team={team}
        ticketTypes={ticketTypes}
        holds={holds}
        fetchHolds={fetchHolds}
        generalAdmissionHolds={generalAdmissionHolds}
        fetchGeneralAdmissionHolds={fetchGeneralAdmissionHolds}
      />
    </>
  );
}

PromoCodesTab.propTypes = {
  currentUser: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  showActivities: PropTypes.array,
  team: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array,
  holds: PropTypes.array,
  generalAdmissionHolds: PropTypes.array,
  fetchGeneralAdmissionHolds: PropTypes.func,
  fetchHolds: PropTypes.func
};

export default PromoCodesTab;
